import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';

/* Import components */
import { UserInfo } from '../User/UserTypes';
import MaybeTooltip from '../Tooltip/MaybeTooltip';
import { Avatar } from '@cube3/cubicle/src/core/atoms/Avatar';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';
import { ContextMenuListItem } from '@cube3/cubicle/src/core/molecules/ContextMenu/ContextMenuListItem';
import { Option } from '@cube3/cubicle/src/core/atoms/Select/Option';
import { Checkbox } from '@cube3/cubicle/src/core/atoms/Checkbox/Checkbox';

/**
 * @summary: UI component (menu) for the account status indicator
 * @author Simon & ??
 */

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    containerAccount: {
      display: 'flex',
      alignItems: 'center',
      margin: '8px 4px'
    },
    checkbox: {
      marginRight: 4
    }
  })
);

interface AccountFlyoutContentProps {
  account: UserInfo;
  user: UserInfo;
  showEmail?: boolean;
  role?: string;
  multipleAccounts: boolean;
  canRemember?: boolean;
  remember: boolean;
  onClickSignIn: () => void;
  onClickAccountSettings?: () => void;
  onClickSignOut: () => void;
  onClickSwitchAccounts?: () => void;
  onClickRemember: () => void;
}

const AccountFlyoutContent: React.FC<AccountFlyoutContentProps> = ({
  user,
  showEmail,
  role,
  multipleAccounts,
  canRemember,
  remember,
  onClickAccountSettings,
  onClickSignIn,
  onClickSignOut,
  onClickSwitchAccounts,
  onClickRemember
}) => {
  const classes = useStyles();

  return (
    <VBox gap={4}>
      <MaybeTooltip
        disabled={!role && !showEmail}
        title={
          role ? (
            <Typography colorVariant="01">Role: {role}</Typography>
          ) : user.email_address ? (
            <Typography colorVariant="01">{user.email_address}</Typography>
          ) : undefined
        }
        placement="left-start"
      >
        <HBox gap={8} className={classes.containerAccount}>
          <Avatar
            userInitial={user.full_name}
            imageUrl={user.profile_picture}
            hideTooltip
            size={SizeVariants.sm}
          />
          <Typography colorVariant="01">{user.full_name}</Typography>
        </HBox>
      </MaybeTooltip>

      <Divider color="line3" />

      {onClickAccountSettings && (
        <ContextMenuListItem
          name="Account settings"
          onClick={onClickAccountSettings}
          leftIcon="settings"
          showChevron={false}
        />
      )}
      {canRemember && (
        <Option
          value="Keep me signed in"
          onClick={onClickRemember}
          startAdornment={
            <span className={classes.checkbox}>
              <Checkbox
                value={remember}
                onChange={onClickRemember}
                size={SizeVariants.sm}
              />
            </span>
          }
        />
      )}

      {(onClickAccountSettings || canRemember) && <Divider color="line3" />}

      {onClickSwitchAccounts && multipleAccounts && (
        <>
          <ContextMenuListItem
            name="Switch workspaces"
            onClick={onClickSwitchAccounts}
            leftIcon="workspaces"
            showChevron={false}
          />

          <Divider color="line3" />
        </>
      )}

      <ContextMenuListItem
        name="Sign out"
        onClick={onClickSignOut}
        leftIcon="logout"
        showChevron={false}
      />
    </VBox>
  );
};

export default AccountFlyoutContent;
